import { BaseError } from 'make-error';
import { Log } from '../../AuxillaryService/Log';
import { toBoolean, toInt } from '../../Utility';
import { isNullish } from '../../Utility/Nullish';
import type { Announcement } from './Announcement';
import {
    AnnouncementContent,
    AnnouncementDefault,
    AnnouncementReachVideo,
    AnnouncementSocialMedia,
    AnnouncementVimeo,
    AnnouncementYoutube,
} from './Announcements';
import type { AnnouncementWidget } from './AnnouncementWidget';
import type { PlayTimes } from './PlayTimes';
import {
    Animation,
    AnnouncementStatus,
    AnnouncementTemplates,
    SM_FACEBOOK,
    SM_INSTAGRAM,
    SM_TWITTER,
} from './types';
import {
    isLegacyManifestContent,
    isLegacyManifestDefault,
    isLegacyManifestDefaultTextbox,
    isLegacyManifestSocialMedia,
    LegacyAnnouncementManifest,
} from './typesLegacyManifest';
import {
    isManifestDefault,
    isManifestKaltura,
    isManifestReachVideo,
    isManifestVimeo,
    isManifestYoutube,
    AnnouncementManifest,
    isManifestVideo,
} from './typesManifest';

export type UnknownManifest = AnnouncementManifest | LegacyAnnouncementManifest;
type UnknownManifestItem = UnknownManifest['announcements'][0];
export const INSUFFICIENT_PLAYLIST_RIGHTS = 'insufficient-playlist-rights' as const;
export const VIDEO_NOT_SUPPORTED = 'video-not-supported' as const;

/**
 * Convert to valid Announcement Status
 */
function announcementStatusFix(value: string) {
    switch (value) {
        case AnnouncementStatus.ACTIVE:
            return AnnouncementStatus.ACTIVE;
        case AnnouncementStatus.APPROVED:
            return AnnouncementStatus.APPROVED;
        case AnnouncementStatus.DELETED:
            return AnnouncementStatus.DELETED;
        case AnnouncementStatus.DRAFT:
            return AnnouncementStatus.DRAFT;
        case AnnouncementStatus.EXPIRED:
            return AnnouncementStatus.EXPIRED;
        case AnnouncementStatus.INACTIVE:
            return AnnouncementStatus.INACTIVE;
        case AnnouncementStatus.NEED_APPROVAL:
            return AnnouncementStatus.NEED_APPROVAL;
        default:
            return AnnouncementStatus.INACTIVE;
    }
}

/**
 * Convert to valid Social Media Template
 */
function legacySocialMediaTemplateFix(value: string) {
    const A = 'a' as const;
    const B = 'b' as const;
    const C = 'c' as const;
    switch (value) {
        case A:
            return A;
        case B:
            return B;
        case C:
            return C;
        default:
            return null;
    }
}

/**
 * Convert to valid Animation type
 */
function announcementAnimationFix(animation: string | null) {
    switch (animation) {
        case 'fade':
            return Animation.Fade;
        case 'slide-right':
            return Animation.SlideRight;
        case 'slide-left':
            return Animation.SlideLeft;
        case 'slide-up':
            return Animation.SlideUp;
        case 'slide-down':
            return Animation.SlideDown;
        default:
            return Animation.None;
    }
}

const LEGACY_TEXTBOX_NONE = '' as const;

/**
 * Convert to valid text alignment
 */
function legacyTextboxAlignFix(value: string) {
    const CENTER = 'center' as const;
    const LEFT = 'left' as const;
    const RIGHT = 'right' as const;
    switch (value) {
        case CENTER:
            return CENTER;
        case LEFT:
            return LEFT;
        case RIGHT:
            return RIGHT;
        default:
            return LEGACY_TEXTBOX_NONE;
    }
}

/**
 * Convert to valid shape
 */
function shapeTypeFix(value: string) {
    const RECT = 'rect' as const;
    const ELLIPSE = 'ellipse' as const;
    const RECTANGLE = 'rectangle' as const;
    switch (value) {
        case RECT:
            return RECT;
        case ELLIPSE:
            return ELLIPSE;
        case RECTANGLE:
            return RECTANGLE;
        default:
            return RECT;
    }
}

/**
 * Infer which type of announcement class is appropriate for the configuration and then return
 * an instance of a specific announcement type.
 * @throws {@link UnsupportedAnnouncementError} when given an unsupported or unknown Announcement
 * configuration.
 */
export function createAnnouncement(
    widget: AnnouncementWidget,
    configuration: UnknownManifestItem,
    playTimes: PlayTimes,
    width: number,
    height: number
): Announcement {
    let timing =
        configuration.ann_seconds !== null && '' !== configuration.ann_seconds
            ? Number(configuration.ann_seconds)
            : 10;

    if (
        isManifestVideo(configuration) &&
        !isNullish(configuration.video_total_duration) &&
        !isNaN(Number(configuration.video_total_duration)) &&
        !isNaN(Number(configuration.video_start_time)) &&
        Number(configuration.video_start_time) > Number(configuration.video_total_duration)
    ) {
        Log.Warning('Video length is shorter than start time', null, __fileline);
        timing = 0;
    }

    const isLegacyManifest = widget.config.is_new_media_library !== true;

    const baseConfig = {
        id: Number(configuration.announcement_id),
        background_color: isNullish(configuration.background_color)
            ? null
            : String(configuration.background_color),
        group_id: isNullish(configuration.group_id) ? null : Number(configuration.group_id),
        order: Number(configuration.announcement_order),
        name: configuration.announcement_name,
        start: configuration.date_start,
        end: configuration.date_end,
        status: announcementStatusFix(configuration.ann_status),
        timing: timing,
        alert:
            typeof configuration.screen_alert === 'string' &&
            configuration.screen_alert.indexOf(widget.config.screenid) > -1,
        updated: Number(configuration.updated_timestamp || 0),
        media_library_item_id: isNullish(configuration.media_library_item_id)
            ? null
            : Number(configuration.media_library_item_id),
        go_full_screen: configuration.go_full_screen,
        width: configuration.width ? toInt(configuration.width) : width,
        height: configuration.height ? toInt(configuration.height) : height,
    };
    if (isLegacyManifestSocialMedia(configuration, isLegacyManifest)) {
        const baseSmConfig = {
            sm_template: legacySocialMediaTemplateFix(configuration.sm_template),
            sm_id: String(configuration.sm_id),
            reach_sm_id: String(configuration.reach_sm_id),
        };
        let smConfig;
        switch (configuration.template) {
            case 'facebook':
                smConfig = {
                    template: SM_FACEBOOK,
                    ...baseConfig,
                    ...baseSmConfig,
                };
                break;
            case 'instagram':
                smConfig = {
                    template: SM_INSTAGRAM,
                    ...baseConfig,
                    ...baseSmConfig,
                };
                break;
            case 'twitter':
                smConfig = {
                    template: SM_TWITTER,
                    ...baseConfig,
                    ...baseSmConfig,
                };
                break;
        }
        return new AnnouncementSocialMedia(smConfig, widget, playTimes);
    } else if (isLegacyManifestContent(configuration, isLegacyManifest)) {
        return new AnnouncementContent(
            {
                template: AnnouncementTemplates.CONTENT,
                ...baseConfig,
                content_type: configuration.content_type,
            },
            widget,
            playTimes
        );
    } else if (isManifestReachVideo(configuration)) {
        return new AnnouncementReachVideo(
            {
                template: AnnouncementTemplates.VIDEO,
                ...baseConfig,
                video_id: configuration.video_id,
                youtube_enable_cc: false, //configuration.youtube_enable_cc,
                video_start_time: Number(configuration.video_start_time),
                video_total_duration: Number(configuration.video_total_duration),
            },
            widget,
            playTimes
        );
    } else if (isManifestYoutube(configuration)) {
        return new AnnouncementYoutube(
            {
                template: AnnouncementTemplates.YOUTUBE,
                ...baseConfig,
                youtubelink: configuration.youtubelink,
                youtube_enable_cc: toBoolean(configuration.youtube_enable_cc),
                video_start_time: Number(configuration.video_start_time),
                video_total_duration: Number(configuration.video_total_duration),
            },
            widget,
            playTimes
        );
    } else if (isManifestKaltura(configuration)) {
        throw new UnsupportedAnnouncementError('Kaltura Announcements are no longer supported.');
    } else if (isManifestVimeo(configuration)) {
        return new AnnouncementVimeo(
            {
                template: AnnouncementTemplates.VIMEO,
                ...baseConfig,
                youtube_enable_cc: toBoolean(configuration.youtube_enable_cc),
                video_start_time: Number(configuration.video_start_time),
                video_total_duration: Number(configuration.video_total_duration),
                vimeo_video_id: configuration.vimeo_video_id,
            },
            widget,
            playTimes
        );
    } else if (
        isLegacyManifestDefault(configuration, isLegacyManifest) ||
        isManifestDefault(configuration)
    ) {
        return new AnnouncementDefault(
            {
                template: AnnouncementTemplates.Default,
                ...baseConfig,
                bgimg: configuration.bgimg ?? '',
                textboxes: configuration.textboxes.map((i) => {
                    const ITALIC = 'italic' as const;
                    const NORMAL = 'normal' as const;
                    if (isLegacyManifestDefaultTextbox(i, isLegacyManifest)) {
                        return {
                            id: Number(i.id),
                            animation: announcementAnimationFix(i.animation),
                            width: String(i.width),
                            height: String(i.height),
                            x: String(i.x),
                            y: String(i.y),
                            z: Number(i.z),
                            text: i.text,
                            align: legacyTextboxAlignFix(i.align),
                            shadow: toBoolean(i.shadow, false),
                            underline: i.underline,
                            italic: i.italic === 'italic' ? ITALIC : NORMAL,
                            bold: i.bold,
                            lineheight: i.lineheight,
                            font: i.font,
                            size: i.size,
                            color: i.color,
                        };
                    } else {
                        return {
                            id: Number(i.id),
                            animation: announcementAnimationFix(i.animation),
                            width: String(i.width),
                            height: String(i.height),
                            x: String(i.x),
                            y: String(i.y),
                            z: Number(i.z),
                            text: i.text,
                            align: LEGACY_TEXTBOX_NONE,
                            shadow: false,
                            underline: LEGACY_TEXTBOX_NONE,
                            italic: NORMAL,
                            bold: LEGACY_TEXTBOX_NONE,
                            lineheight: LEGACY_TEXTBOX_NONE,
                            font: LEGACY_TEXTBOX_NONE,
                            size: LEGACY_TEXTBOX_NONE,
                            color: LEGACY_TEXTBOX_NONE,
                        };
                    }
                }),
                shapes: configuration.shapes.map((i) => {
                    return {
                        id: Number(i.id),
                        animation: announcementAnimationFix(i.animation),
                        width: String(i.width),
                        height: String(i.height),
                        x: String(i.x),
                        y: String(i.y),
                        z: Number(i.z),
                        type: shapeTypeFix(i.type),
                        color: i.color,
                    };
                }),
                images: configuration.images.map((i) => {
                    return {
                        id: Number(i.id),
                        animation: announcementAnimationFix(i.animation),
                        width: String(i.width),
                        height: String(i.height),
                        x: String(i.x),
                        y: String(i.y),
                        z: String(i.z),
                        imgpath: i.imgpath,
                    };
                }),
            },
            widget,
            playTimes
        );
    } else if (
        configuration.template === INSUFFICIENT_PLAYLIST_RIGHTS ||
        configuration.template === VIDEO_NOT_SUPPORTED
    ) {
        return new AnnouncementDefault(
            {
                template: configuration.template,
                ...baseConfig,
            },
            widget,
            playTimes
        );
    } else {
        throw new UnsupportedAnnouncementError(
            `Announcement type '${configuration.template}' not supported.`
        );
    }
}

export class UnsupportedAnnouncementError extends BaseError {
    constructor(message: string) {
        super(message);
    }
}
